import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { FilePdf, MagnifyingGlass, Check, PaperPlaneTilt } from "@phosphor-icons/react";
import PdfBrandingDialog, { downloadBlob } from "../hs/PdfBrandingDialog";
import { fmtDate } from "../../lib/dateUtil";
import { openAuthFile } from "../../lib/fileOpen";
import SendPackPanel from "./SendPackPanel";

const SECTIONS = [["accidents", "Accident & incident statistics"], ["competency", "Competency & training summary"], ["operations", "Inspections, RAMS & toolbox talks"], ["receipts", "Policy read receipts"], ["matrix", "Named competency matrix (appendix)"]];

/** One-click HSE tender pack for a client: policy in full, register of approved documents, stats — filed under the client. */
export default function ClientHsePackDialog({ open, onOpenChange, client: preset, onGenerated }) {
  const [client, setClient] = useState(preset || null);
  const [search, setSearch] = useState("");
  const [matches, setMatches] = useState([]);
  const [preview, setPreview] = useState(null);
  const [months, setMonths] = useState(12);
  const [options, setOptions] = useState({ accidents: true, competency: true, operations: true, receipts: true, matrix: true });
  const [note, setNote] = useState("");
  const [save, setSave] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sendPack, setSendPack] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setClient(preset || null); setSearch(""); setMatches([]); setNote(""); setSendPack(null); } }, [open, preset?.id]);
  useEffect(() => {
    if (!open) return;
    API.get(`/hse/pack/preview?months=${months}${client ? `&client_id=${client.id}` : ""}`).then(({ data }) => setPreview(data)).catch(() => {});
  }, [open, months, client]);
  useEffect(() => {
    if (!open || preset || search.trim().length < 2) { setMatches([]); return undefined; }
    const t = setTimeout(() => API.get(`/clients?search=${encodeURIComponent(search.trim())}`).then(({ data }) => setMatches((data.clients || []).slice(0, 8))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [search, open, preset]);

  const name = (c) => c?.name || c?.company_name || [c?.first_name, c?.last_name].filter(Boolean).join(" ") || "Client";
  const [brandOpen, setBrandOpen] = useState(false);
  const generate = async (branding) => {
    if (!client) { toast.error("Pick the client first"); return; }
    setBusy(true);
    try {
      const resp = await API.post("/hse/pack", { client_id: client.id, months, options, cover_note: note, save, ...(branding ? { branding, save_for_client: !!branding.save_for_client } : {}) }, { responseType: "blob" });
      const ref = resp.headers["x-pack-reference"] || "HSE_Pack";
      downloadBlob(resp.data, `${ref}_HSE_Pack_${name(client).replace(/\s+/g, "_")}.pdf`);
      toast.success(`${ref} generated${save ? " and filed under " + name(client) : ""}`);
      onGenerated?.();
      if (save && resp.headers["x-pack-id"]) { setSendPack({ id: resp.headers["x-pack-id"], reference: ref, justGenerated: true }); setPreview(null); API.get(`/hse/pack/preview?months=${months}&client_id=${client.id}`).then(r => setPreview(r.data)).catch(() => {}); }
      else onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hse-pack-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><FilePdf size={18} /> Client HSE tender pack</DialogTitle>
          <DialogDescription className="text-xs">Cover, contents, your Health &amp; Safety Policy in full, a register of every other approved document, compliance coverage, statistics and a signed declaration.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Prepared for</Label>
            {client ? (
              <div className="flex items-center gap-2 border border-border rounded-md px-3 h-10 text-sm" data-testid="hse-pack-client">
                <Check size={14} className="text-emerald-600" /><span className="flex-1 font-medium">{name(client)}</span>
                {!preset && <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setClient(null)} data-testid="hse-pack-client-change">Change</button>}
              </div>
            ) : (
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-3 text-muted-foreground" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients by name…" className="h-10 pl-8" autoFocus data-testid="hse-pack-client-search" />
                {matches.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-popover border border-border rounded-md shadow-md max-h-56 overflow-y-auto" data-testid="hse-pack-client-results">
                    {matches.map(c => <button key={c.id} type="button" onClick={() => { setClient(c); setMatches([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-muted" data-testid={`hse-pack-client-${c.id}`}>{name(c)}{c.email ? <span className="text-xs text-muted-foreground"> · {c.email}</span> : null}</button>)}
                  </div>
                )}
              </div>
            )}
          </div>
          {preview && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="hse-pack-preview">
              <Stat label="Approved documents" value={preview.approved_documents} note={preview.has_policy ? "incl. H&S Policy" : "no H&S Policy yet"} warn={!preview.has_policy} />
              <Stat label="Operatives" value={preview.operatives} note={`${preview.qualifications} qualifications`} />
              <Stat label="Incidents in period" value={preview.incidents_in_period} note={`${months} months`} />
              <Stat label="Company profile" value={preview.profile_complete ? "Ready" : "Missing"} note={preview.company_name || "fill in the HSE profile"} warn={!preview.profile_complete} />
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Statistics period</Label>
              <select value={months} onChange={e => setMonths(Number(e.target.value))} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="hse-pack-months">
                <option value={6}>Last 6 months</option><option value={12}>Last 12 months</option><option value={24}>Last 24 months</option><option value={36}>Last 36 months</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Include</Label>
              <div className="space-y-1">
                {SECTIONS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!options[k]} onChange={e => setOptions(o => ({ ...o, [k]: e.target.checked }))} data-testid={`hse-pack-opt-${k}`} /> {label}</label>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Covering note (optional, printed on the cover)</Label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="e.g. Submitted in support of our tender for the 2027 planned maintenance contract." className="text-sm" data-testid="hse-pack-note" />
          </div>
          <label className="flex items-center gap-2 text-xs"><Switch checked={save} onCheckedChange={setSave} data-testid="hse-pack-save" /> File the PDF under the client's documents as well as downloading it</label>
          {preview?.previous_packs?.length > 0 && (
            <div className="text-xs" data-testid="hse-pack-previous">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Previous packs</p>
              <ul className="space-y-0.5">
                {preview.previous_packs.map(p => (
                  <li key={p.id} className="flex flex-wrap items-center gap-x-2">
                    {p.file_url ? <button type="button" onClick={() => openAuthFile(p.file_url)} className="text-primary hover:underline font-mono">{p.reference}</button> : <span className="font-mono" title="Generated without filing — download only">{p.reference}</span>}
                    <span className="text-muted-foreground">{fmtDate(p.created_at)}{p.client_name && !preset ? ` · ${p.client_name}` : ""}{p.generated_by ? ` · ${p.generated_by}` : ""}{!p.file_url ? " · not filed" : ""}{p.sent?.length ? ` · sent ${p.sent.length}×` : ""}</span>
                    {p.file_url && <button type="button" onClick={() => setSendPack({ id: p.id, reference: p.reference })} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`hse-pack-send-${p.id}`}><PaperPlaneTilt size={12} /> Send</button>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {sendPack && (
            <div className="space-y-2" data-testid="hse-pack-send-section">
              {sendPack.justGenerated && <p className="text-xs text-emerald-700 font-medium" data-testid="hse-pack-generated-msg">{sendPack.reference} is downloaded and filed. Send it to the client now, or close.</p>}
              <SendPackPanel packId={sendPack.id} onSent={() => { API.get(`/hse/pack/preview?months=${months}${client ? `&client_id=${client.id}` : ""}`).then(r => setPreview(r.data)).catch(() => {}); }} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{sendPack ? "Close" : "Cancel"}</Button>
          <Button onClick={() => setBrandOpen(true)} disabled={busy || !client} data-testid="hse-pack-generate-btn"><FilePdf size={14} className="mr-1" /> {busy ? "Building pack…" : "Generate pack"}</Button>
        </DialogFooter>
      </DialogContent>
      <PdfBrandingDialog open={brandOpen} onOpenChange={setBrandOpen} clientId={client?.id} onDownload={generate} title="HSE pack branding"
        description={`Brand the pack for ${name(client)} — their saved branding can be loaded below. Use default for your own company letterhead.`} />
    </Dialog>
  );
}

function Stat({ label, value, note, warn }) {
  return (
    <div className={`border rounded-md p-2 ${warn ? "border-amber-300 bg-amber-50/50" : "border-border"}`}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-base font-semibold leading-tight">{value}</p>
      <p className="text-[11px] text-muted-foreground">{note}</p>
    </div>
  );
}
